import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { CalendarClock, Pencil, SlidersHorizontal } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/atoms/tabs';
import { DetailPageLayout } from '@/components/layouts';
import { EstimateSettingsEditSheet } from '@/components/settings/estimates/EstimateSettingsEditSheet';
import { EstimateSettingsReadOnlyTabs } from '@/components/settings/estimates/EstimateSettingsReadOnlyTabs';
import type { EstimateSettingsTab } from '@/components/settings/estimates/types';
import { Fullbleed } from '@/contexts/fullbleed';

export const Route = createFileRoute('/_dashboard/dashboard/settings/estimates')({
  component: EstimateSettingsPage,
});

const TABS: ReadonlyArray<{
  value: EstimateSettingsTab;
  label: string;
  icon: LucideIcon;
}> = [
  { value: 'config', label: 'Configuration', icon: SlidersHorizontal },
  { value: 'paymentSchedule', label: 'Payment Schedules', icon: CalendarClock },
];

function EstimateSettingsPage() {
  const [tab, setTab] = useState<EstimateSettingsTab>('config');
  const [editOpen, setEditOpen] = useState(false);

  return (
    <>
      <Fullbleed />
      <DetailPageLayout
        header={
          <>
            <div className="min-w-0">
              <h1 className="truncate text-base font-semibold">Estimates</h1>
              <p className="text-xs text-muted-foreground">
                Configure estimate defaults and payment schedules. Document appearance lives in
                Document designs.
              </p>
            </div>
            <Button
              variant="outline"
              size="icon-sm"
              className="shrink-0"
              title="Edit"
              aria-label="Edit estimate settings"
              onClick={() => setEditOpen(true)}
            >
              <Pencil className="size-3.5" />
            </Button>
          </>
        }
      >
        <Tabs value={tab} onValueChange={(value) => setTab(value as EstimateSettingsTab)}>
          <TabsList
            variant="line"
            className="h-auto w-full justify-start rounded-none border-b px-0 pb-0"
          >
            {TABS.map((item) => {
              const Icon = item.icon;
              return (
                <TabsTrigger key={item.value} value={item.value}>
                  <Icon className="size-3.5" />
                  {item.label}
                </TabsTrigger>
              );
            })}
          </TabsList>

          <EstimateSettingsReadOnlyTabs />
        </Tabs>
      </DetailPageLayout>
      <EstimateSettingsEditSheet open={editOpen} tab={tab} onOpenChange={setEditOpen} />
    </>
  );
}
